import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MailCheck, Music2, RotateCcw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

const COOLDOWN_SECONDS = 60;

/**
 * The post-register "Check your email" screen. Reachable two ways:
 *   1. After /register, when the backend returns requires_verification.
 *   2. After /login, when the backend returns 403 email_not_verified.
 *
 * Carries the email forward through router state so the user can resend
 * the link without retyping. Spam-aware copy is shown front and centre.
 */
export default function CheckEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || "";
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!email) {
      // Direct visit with no email in state — send them to login.
      navigate("/login", { replace: true });
    }
  }, [email, navigate]);

  // Cooldown ticker — purely visual; the backend enforces the real limit.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success("Verification email sent — check your inbox (and junk).");
      setCooldown(COOLDOWN_SECONDS);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = formatApiErrorDetail(detail) || e.message;
      if (e.response?.status === 429) {
        // Rate-limit response from backend — show as info, not error.
        toast.info(msg);
        setCooldown(COOLDOWN_SECONDS);
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory grid place-items-center p-4">
      <div className="w-full max-w-md card-base p-6 sm:p-8 animate-fade-up" data-testid="check-email-card">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <span className="w-9 h-9 rounded-xl bg-sage-500 text-white grid place-items-center">
            <Music2 size={20} />
          </span>
          <span className="font-heading font-semibold text-lg">AMDS</span>
        </Link>

        <div className="w-14 h-14 rounded-2xl bg-sage-100 text-sage-700 grid place-items-center mb-4">
          <MailCheck size={26} />
        </div>

        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
          Check your email
        </h1>
        <p className="text-ink-700 text-sm mt-2 leading-relaxed">
          We've sent a verification link to <strong data-testid="check-email-address">{email}</strong>.
          Click it to activate your account and start using your diary.
        </p>

        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 text-[13px] text-ink-700" data-testid="spam-notice">
          <strong>Don't see it?</strong> Please check your <strong>junk or spam folder</strong> within a few minutes. If you find it there, mark it as "not junk" so future bookings and dep requests reach your inbox.
        </div>

        <div className="mt-5 space-y-2.5">
          <button
            onClick={resend}
            disabled={cooldown > 0 || busy}
            className="btn-primary w-full justify-center"
            data-testid="resend-verification-btn"
          >
            <RotateCcw size={16} />
            {cooldown > 0 ? `Resend in ${cooldown}s` : (busy ? "Sending…" : "Resend verification email")}
          </button>

          <button
            onClick={() => navigate("/register", { replace: true })}
            className="btn-ghost w-full justify-center text-sm"
            data-testid="wrong-email-btn"
          >
            <ArrowLeft size={14} /> Wrong email? Use a different one
          </button>

          <Link to="/login" className="block text-center text-xs text-ink-500 mt-1" data-testid="back-to-login-link">
            Already verified? Sign in →
          </Link>
        </div>
      </div>
    </div>
  );
}
